/**
 * @description This utility is used to log messages to the console based on the environment.
 * @param message - The message to log
 * @param data - The data to log
 * @type {LogLevel} - The level of the message
 * @returns void
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * @description Configuration for the logger 
 * @interface LoggerConfig
 * @property {boolean} enabled - Whether the logger is enabled
 * @property {LogLevel} level - The level of the message
 * @property {string} prefix - The prefix of the message
 */
interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix?: string;
}

/**
 * @description Default configuration for the logger
 * @constant {LoggerConfig} defaultConfig
 * @property {boolean} enabled - Whether the logger is enabled
 * @property {LogLevel} level - The level of the message
 * @property {string} prefix - The prefix of the message
 */
const defaultConfig: LoggerConfig = {
  enabled: true,
  level: 'debug',
  prefix: '[Extension]'
};

/**
 * Logger class for conditional logging
 */
class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * Updates logger configuration
   */
  configure(config: Partial<LoggerConfig>) {
    this.config = { ...this.config, ...config };
  }

  /**
   * Logs debug messages (lowest priority)
   */
  debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  /**
   * Logs informational messages
   */
  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  /**
   * Logs warning messages
   */
  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  /**
   * Logs error messages (highest priority)
   */
  error(message: string, data?: any) {
    this.log('error', message, data);
  }

  /**
   * Internal logging method
   */
  private log(level: LogLevel, message: string, data?: any) {
    if (!this.config.enabled) return;
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const prefix = this.config.prefix ? `${this.config.prefix} ` : '';
    const formattedMessage = `${prefix}[${level.toUpperCase()}] ${timestamp} - ${message}`;

    switch (level) {
      case 'debug':
        console.log(formattedMessage, data || '');
        break;
      case 'info':
        console.info(formattedMessage, data || '');
        break;
      case 'warn':
        console.warn(formattedMessage, data || '');
        break;
      case 'error':
        console.error(formattedMessage, data || '');
        break;
    }
  }

  /**
   * Determines if message should be logged based on level
   */
  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }
}

/**
 * Default logger instance
 */
export const logger = new Logger();

/**
 * Creates a new logger instance with custom configuration
 */
export const createLogger = (config: Partial<LoggerConfig> = {}) => {
  return new Logger(config);
};